import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { generateCUID } from '@alexis/helpers/identity';
import { translateText } from '@alexis/helpers/translation';
import LegView from '@pages/FlightSearchResult/components/TripCard/LegView';
import { providerCodesForBaggageFilter } from '@pages/FlightSearchResult/constants';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { uniq, uniqBy } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Price from '@components/Price';

import {
  FLIGHT_SCHEDULE_NORMALISATION,
  OFFER_ENGINE,
  TEST_FEATURES_PARAM,
  MOCK_BAGGAGE_FILTER,
} from '@constants/flight';
import { GTM_ID } from '@constants/general';

import {
  format,
  isBigAmount,
  getHeightOfHighlightTags,
  hasBnplPaymentOptionForTripCardDisplay,
} from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';
import { getBrowserFontSizeConstant } from '@helpers/utils';
import { webEngageTrackFareClick, webEngageTrackPurchase } from '@helpers/webEngage';

import CheckedBaggageIcon from '@icons/checked-baggage-included.svg';
import InfoIcon from '@icons/info.svg';

import { FlightSearchResultImpressionLogging } from '@hooks/useFlightSearchResultImpressionLogging';

import {
  getCurrencyState,
  getCurrentSiteState,
  getLocaleState,
  getPageViewIdState,
  getPriceOptionsState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import type { TripViewModel } from '@wegoTypes/flights/tripViewModel';

import styles from './TripCard.module.scss';

export interface TripCardProps {
  cardPosition?: 'first' | 'middle' | 'last' | '';
  gtmAuth: string;
  gtmPreview: string;
  hasNoTopBorder?: boolean;
  impressionLogging: FlightSearchResultImpressionLogging;
  isGroupExpanded?: boolean;
  paymentMethodsFromApi: Array<HomepageFlightsPaymentMethod>;
  similarOptionsCount: number;
  tripViewModel: TripViewModel;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  genzoTrackApplyFilterSearchView(): void;
  onSimilarOptionsClick?(): void;
  onTripCardMouseHover(tripCardMouseHovered: boolean): void;
  onTripViewModelSelect(tripViewModel: TripViewModel): void;
}

const TripCard: React.FC<TripCardProps> = ({
  cardPosition,
  gtmAuth,
  gtmPreview,
  hasNoTopBorder,
  impressionLogging,
  isGroupExpanded,
  paymentMethodsFromApi,
  similarOptionsCount,
  tripViewModel,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  genzoTrackApplyFilterSearchView,
  onSimilarOptionsClick,
  onTripCardMouseHover,
  onTripViewModelSelect,
}) => {
  const { search, exchangeRate } = useFlightSearchResults();
  const currentSite = useSelector(getCurrentSiteState);
  const { selectedLegs, setSelectedLegs } = useFlightSearchFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  const currency = useSelector(getCurrencyState);
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);
  const showTotalPrice = useSelector(getPriceOptionsState).showTotalPrice;
  const user = useSelector(getUserState);
  const pageViewId = useSelector(getPageViewIdState).home;

  const baggageInfoFares = useMemo(
    () =>
      tripViewModel?.filteredFares.filter((fare) =>
        providerCodesForBaggageFilter.includes(fare.provider?.code),
      ),
    [tripViewModel?.filteredFares],
  );

  const { passengerCount, isMultiCity } = useFlightSearchPageParams();

  const [isCardHovered, setIsCardHovered] = useState<boolean>(false);

  const testFeatures = useMemo(() => {
    const testFeaturesSearchParam = searchParams.get(TEST_FEATURES_PARAM);

    return testFeaturesSearchParam ? testFeaturesSearchParam.split(',') : [];
  }, [searchParams]);

  const isShowBaggageFilter = testFeatures.includes(MOCK_BAGGAGE_FILTER);

  const hasBaggageInfo = !isMultiCity && isShowBaggageFilter && baggageInfoFares?.length;
  const baggageInfoText = `${baggageInfoFares?.length} fare${
    (baggageInfoFares?.length || 0) > 1 ? 's' : ''
  }`;

  const flightScheduleNormalisationIndicatorEnabled = testFeatures.includes(
    FLIGHT_SCHEDULE_NORMALISATION,
  );

  const fareOfferEngineIndicatorEnabled = testFeatures.includes(OFFER_ENGINE);

  const browserFontSizeConstant = getBrowserFontSizeConstant();

  const fares = useMemo(() => {
    return tripViewModel.filteredFares
      .map((fareViewModel) => fareViewModel.fare)
      .sort((fareA, fareB) => fareA.price.amount - fareB.price.amount);
  }, [tripViewModel.filteredFares]);

  const fareProviders = useMemo(() => {
    return uniqBy(
      tripViewModel.filteredFares.map((fareViewModel) => fareViewModel.provider),
      'code',
    );
  }, [tripViewModel.filteredFares]);

  const fareProvider = useMemo(() => {
    if (fares.length) {
      return fareProviders.find((fareProvider) => {
        return fares[0].providerCode === fareProvider?.code;
      });
    }
  }, [fareProviders, fares]);

  const hasBnpl = useMemo(() => {
    return hasBnplPaymentOptionForTripCardDisplay(paymentMethodsFromApi, fares);
  }, [fares, paymentMethodsFromApi]);

  const highlightTags = useMemo(() => {
    return uniq(tripViewModel.tripCardTags);
  }, [tripViewModel.tripCardTags]);

  const hasBestTag = useMemo(() => {
    return highlightTags.includes('best');
  }, [highlightTags]);

  const cheapestTag = useMemo(() => {
    if (highlightTags.includes('cheapest')) {
      return translations.cheapest;
    }
    if (highlightTags.includes('cheapest_direct')) {
      return translations.cheapest_direct;
    }
  }, [highlightTags, translations.cheapest, translations.cheapest_direct]);

  const viewDealsPaddingTop = useMemo<number>(() => {
    const hightlightTagHeight = getHeightOfHighlightTags(highlightTags);

    const heightOfTags = ((hasBnpl ? 26 : 0) + hightlightTagHeight) * browserFontSizeConstant;

    const paddingTop = 16 + (heightOfTags < 36 ? 36 - heightOfTags : 0) / 2;

    return paddingTop;
  }, [hasBnpl, highlightTags, browserFontSizeConstant]);

  const handleOnLegFilter = useCallback(
    (index: number, legId: string): void => {
      const selectedLegIndex = selectedLegs.findIndex(
        (selectedLeg) => selectedLeg.index === index && selectedLeg.legId === legId,
      );
      if (selectedLegIndex === -1) {
        setSelectedLegs([...selectedLegs, { index, legId }]);
      } else {
        selectedLegs.splice(selectedLegIndex, 1);
        setSelectedLegs([...selectedLegs]);
      }

      genzoTrackApplyFilterSearchView();

      const gtmData = {
        event: 'eventTracked',
        eventAction: 'filter',
        eventCategory: 'flights',
        eventLabel: 'leg-select',
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);

      if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
        const eventData: any = {
          id: pageViewId,
          category: 'search_results_options',
          object: 'filter_this_flight_only',
          action: selectedLegIndex === -1 ? 'checked' : 'unchecked',
          value: `${index + 1}`,
        };

        const eventId = generateCUID();

        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          user?.userHash,
          eventData,
          eventId,
        );
      }
    },
    [
      selectedLegs,
      gtmAuth,
      gtmPreview,
      pageViewId,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
      user?.userHash,
    ],
  );

  const handleOnAirportCodeClick = useCallback(
    (searchParam: 'origin' | 'destination', airportCode: string): void => {
      if (searchParams.get(searchParam)) {
        searchParams.delete(searchParam);
      } else {
        searchParams.append(searchParam, airportCode);
      }

      setSearchParams(searchParams, { replace: true });

      genzoTrackApplyFilterSearchView();
    },
    [searchParams, setSearchParams],
  );

  const handleOnCardClick = useCallback(
    (tripViewModel: TripViewModel): void => {
      impressionLogging.handleTripCardClick(tripViewModel);

      if (!tripViewModel.sponsored && search?.id) {
        searchParams.delete('selected_trip_id');
        searchParams.delete('search_id');
        searchParams.append('selected_trip_id', tripViewModel.tripId);
        searchParams.append('search_id', search.id);

        setSearchParams(searchParams, { replace: true });

        onTripViewModelSelect(tripViewModel);
      }

      const gtmData = {
        event: 'eventTracked',
        eventAction: 'detail',
        eventCategory: 'flights',
        eventLabel: 'flight-card',
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
    },
    [search?.id, gtmAuth, gtmPreview, searchParams, setSearchParams],
  );

  const isLegChecked = useCallback<(index: number, legId: string) => boolean>(
    (index: number, legId: string) => {
      return selectedLegs.some(
        (selectedLeg) => selectedLeg.index === index && selectedLeg.legId === legId,
      );
    },
    [selectedLegs],
  );

  const handleOnSimilarOptionsClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();

      onSimilarOptionsClick?.();
    },
    [onSimilarOptionsClick],
  );

  const handleInlineSponsoredAdClick = useCallback<() => void>(() => {
    window.open(tripViewModel.filteredFares[0]?.fare.handoffUrl, '_blank');

    if (search) {
      const { legs, adultsCount, childrenCount, infantsCount, cabin } = search;

      webEngageTrackFareClick(
        currentSite.countryCode,
        locale,
        adultsCount,
        childrenCount,
        infantsCount,
        cabin,
        legs,
        tripViewModel.legs,
        tripViewModel.tripId,
        tripViewModel.filteredFares.map((filteredFare) => {
          return { ...filteredFare.fare, provider: filteredFare.provider };
        }),
        {
          ...tripViewModel.filteredFares[0]?.fare,
          provider: tripViewModel.filteredFares[0]?.provider,
        },
      );

      webEngageTrackPurchase();
    }
  }, [
    currentSite.countryCode,
    search,
    locale,
    tripViewModel.filteredFares,
    tripViewModel.legs,
    tripViewModel.tripId,
  ]);

  const handleMouseEnterOnCard = useCallback<() => void>(() => {
    setIsCardHovered(!tripViewModel.sponsored);
    onTripCardMouseHover(true);
  }, [onTripCardMouseHover, tripViewModel.sponsored]);

  const handleMouseLeaveOnCard = useCallback<() => void>(() => {
    setIsCardHovered(false);

    onTripCardMouseHover(false);
  }, [onTripCardMouseHover]);

  const computeFeeText = useCallback(
    (fare: FlightSearchResultFare | undefined, currency: Currency): string => {
      if (!fare) {
        return '';
      }

      if (fare.paymentFees.length === 0) {
        return translations.unknow_payment_fee as string;
      }

      const fee = showTotalPrice ? fare.paymentFees[0].totalAmount : fare.paymentFees[0].amount;

      let feeString = format(fee, currency, locale);

      isBigAmount(fare.paymentFees[0].amount, currency) && (feeString += '.000');

      feeString = currency.symbolFirst ? currency.symbol + feeString : feeString + currency.symbol;
      return feeString;
    },
    [locale, showTotalPrice, translations.unknow_payment_fee],
  );

  if (tripViewModel.isPlaceholder) {
    return <div />;
  }

  return (
    <div
      data-pw='flightSearchResults_tripCard'
      data-testid='trip-card'
      data-trip-id={tripViewModel.tripId}
      className={clsx(
        styles.container,
        !!tripViewModel.sponsored && styles.isSponsoredAd,
        !!cardPosition && styles[cardPosition],
        hasNoTopBorder && styles.hasNoTopBorder,
        flightScheduleNormalisationIndicatorEnabled &&
          tripViewModel.normalizedFlight &&
          styles.normalized,
      )}
      style={
        tripViewModel.sponsored
          ? { border: `2px solid ${tripViewModel.sponsored!.brandingColor}` }
          : {}
      }
      onClick={tripViewModel.sponsored ? handleInlineSponsoredAdClick : undefined}
    >
      {/* Section sponsored card */}
      {tripViewModel.sponsored ? (
        <div
          data-pw='tripCard_sponsoredAdBanner'
          className={styles.sponsoredAd}
          style={{ backgroundColor: tripViewModel.sponsored.brandingColor }}
        >
          <img
            src={cloudinaryImageTransformations(
              tripViewModel.sponsored.partnerLogoUrl,
              'w_200',
              'h_82',
              'c_fit',
              'f_auto',
              'fl_lossy',
              'q_auto:low',
            )}
          />

          <div className={styles.brandLabelsContainer}>
            <div className={styles.brandHeader}>{tripViewModel.sponsored.brandingHeader}</div>
            <div className={styles.brandDescription}>
              {tripViewModel.sponsored.brandingDescription}
            </div>
          </div>

          <div className={styles.tag}>{translations.flights_inline_ads_sponsored_tag}</div>
        </div>
      ) : null}

      {/* Section: Trip Card */}
      <div
        className={clsx(
          styles.tripDetailsCard,
          !!tripViewModel.sponsored && styles.isSponsoredAdTripCard,
          !(similarOptionsCount > 0 || !!isGroupExpanded) && !cardPosition && styles.roundedBorder,
        )}
        onMouseEnter={handleMouseEnterOnCard}
        onMouseLeave={handleMouseLeaveOnCard}
        onClick={() => handleOnCardClick(tripViewModel)}
      >
        {/* Section: Trip View Model */}
        <div className={styles.legContainer}>
          {tripViewModel.legs.map((legViewModel, index) => {
            return (
              <LegView
                key={index}
                index={index}
                isOfferEngine={
                  fareOfferEngineIndicatorEnabled &&
                  fares[0]?.legsSourceTypes[index] === 'OFFER_ENGINE'
                }
                isFareCache={
                  fareOfferEngineIndicatorEnabled &&
                  fares[0]?.legsSourceTypes[index] === 'FARE_CACHE'
                }
                isCardHovered={isCardHovered}
                isLegChecked={isLegChecked(index, legViewModel.leg.id)}
                isSponsoredAd={!!tripViewModel.sponsored}
                legViewModel={legViewModel}
                tripViewModelLegLength={tripViewModel.legs.length}
                onAirportCodeClick={handleOnAirportCodeClick}
                onLegToggle={handleOnLegFilter}
              />
            );
          })}
        </div>

        {/* Section: Trip deals */}
        <div
          data-pw='tripCard_fareDetailsContainer'
          className={styles.deals}
          style={{
            paddingTop: `${viewDealsPaddingTop}px`,
            paddingBottom: `${viewDealsPaddingTop}px`,
          }}
        >
          {tripViewModel.tripCardTags?.length && (
            <div className={styles.itineraryTags}>
              {hasBestTag && (
                <div className={clsx(styles.tags, styles.best)}>{translations.best_value}</div>
              )}
              {!!cheapestTag && (
                <div className={clsx(styles.tags, styles.cheapest)}>{cheapestTag}</div>
              )}
            </div>
          )}

          <span data-pw='tripCard_fareInfo' className={styles.fee}>
            {isCardHovered
              ? translateText(
                  translations.incl_payment_fee,
                  locale,
                  computeFeeText(fares[0], currency),
                )
              : fareProviders.length > 1
              ? translateText(translations.from_website, locale, fareProviders.length)
              : fareProvider
              ? fareProvider.name
              : ''}
          </span>

          {hasBaggageInfo ? (
            <div>
              <CheckedBaggageIcon className={styles.baggageIcon} />
              <span className={styles.baggageInfoText}>{baggageInfoText}</span>
            </div>
          ) : null}

          <div className={styles.priceContainer}>
            {fares.length > 0 ? (
              <Price
                dataPw='tripCard_price'
                isInline
                className={styles.price}
                price={convertFlightSearchResultFarePriceToPrice(
                  fares[0].price,
                  passengerCount,
                  showTotalPrice,
                  true,
                )}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />
            ) : (
              ''
            )}
          </div>

          <span data-pw='tripCard_priceType' className={styles.totalPrice}>
            {showTotalPrice ? translations.total_price : translations.per_person}
          </span>

          {hasBnpl ? (
            <div className={styles.bnplLabel}>
              <span>{translations.lbl_interest_free_payments}</span>

              <div className={styles.tooltipContainer}>
                <InfoIcon className={styles.icon} />

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  <span className={styles.bnplTooltipHeader}>{translations.how_it_works}</span>

                  <ol>
                    <li>
                      <div className={styles.flexBox}>
                        <span>{translations.choose}</span>

                        <img src='https://assets.wego.com/image/upload/v1675920776/payment_logos/3_interest-free_payments.png' />

                        <span>{translations.at_checkout}</span>
                      </div>
                    </li>

                    <li>{translations.redirect_to_tabby}</li>

                    <li>
                      <div className={clsx(styles.flexBox, styles.payWith)}>
                        {translations.choose_pay_with}

                        <img src='https://assets.wego.com/image/upload/v1495167459/payment_logos/VISA_CREDIT_CARD.png' />

                        <img src='https://assets.wego.com/image/upload/v1495167459/payment_logos/MASTERCARD_CREDIT_CARD.png' />

                        <img src='https://assets.wego.com/image/upload/v1660734745/payment_logos/apple_pay.png' />
                      </div>
                    </li>

                    <li>{translations.tabby_sms_reminder}</li>
                  </ol>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Section: Similar Options */}
      {similarOptionsCount > 0 || !!isGroupExpanded ? (
        <div
          data-pw='tripCard_similarOptionsBtn'
          className={styles.similarOptions}
          onClick={handleOnSimilarOptionsClick}
        >
          {isGroupExpanded
            ? translateText(translations.less_flight_combination, locale, similarOptionsCount)
            : translateText(translations.flight_combinations, locale, similarOptionsCount)}

          <i className={clsx(styles.chevron, !isGroupExpanded && styles.bottom)}></i>
        </div>
      ) : null}
    </div>
  );
};

export default TripCard;
